import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography, Snackbar, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils/formatters';
import { useConversation } from '../../contexts/ConversationContext';
import ChatHeader from '../../components/ChatHeader';
import ChatMessage from '../../components/ChatMessage';
import LeadActionsMenu from '../../components/LeadActionsMenu';
import TransferLeadDialog from '../../components/TransferLeadDialog';
import { transformConversation } from '../Leads/utils/leadTransform';
import { useReleaseLead, useCloseLead, useReopenLead } from '../Leads/hooks/useLeadsQuery';
import { useAcceptConversation } from './hooks/useChatPreviewQuery';

const ChatPreview = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [copied, setCopied] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  // Ação em curso (accept/release/close/reopen) → spinner no item do menu.
  const [pendingAction, setPendingAction] = useState(null);
  // Só rola para o fim automaticamente quando o usuário já está perto do fim;
  // evita o "flick" que puxava a tela para a última mensagem a cada polling.
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageRefs = useRef({});
  const topObserverTarget = useRef(null);
  // scrollHeight capturado antes de prepender mensagens antigas (reancoragem).
  const prevScrollHeightRef = useRef(null);

  const {
    conversation,
    latestMessages: latestMessagesRaw,
    olderPages: olderPagesRaw,
    loading,
    loadingOlder: loadingMore,
    hasOlderMessages,
    load,
    loadOlderMessages,
    poll,
  } = useConversation();

  const acceptMutation = useAcceptConversation();
  const releaseMutation = useReleaseLead();
  const closeMutation = useCloseLead();
  const reopenMutation = useReopenLead();

  // Load on screen entry
  useEffect(() => {
    if (id) load(id);
  }, [id, load]);

  // Polling every 6s
  useEffect(() => {
    const interval = setInterval(poll, 6000);
    return () => clearInterval(interval);
  }, [poll]);

  // Auto-scroll para o fim: no load inicial e nos polls, mas só quando o
  // usuário já está perto do fim (shouldAutoScroll). Se ele rolou para cima
  // lendo o histórico, mensagens novas do polling não puxam mais a tela.
  useEffect(() => {
    if (shouldAutoScroll && messagesEndRef.current && !loading) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
  }, [latestMessagesRaw, shouldAutoScroll, loading]);

  // Rastreia a posição do scroll. Depende de loading: o container só existe
  // depois que o loading termina, então o listener é anexado nesse momento.
  useEffect(() => {
    if (loading) return;
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      setShouldAutoScroll(distanceFromBottom < 100);
    };

    handleScroll();
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [loading]);

  // Reancora o scroll após prepender mensagens antigas, evitando o "pulo".
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || prevScrollHeightRef.current == null) return;
    const delta = container.scrollHeight - prevScrollHeightRef.current;
    if (delta > 0) container.scrollTop += delta;
    prevScrollHeightRef.current = null;
  }, [olderPagesRaw]);

  // Observer para carregar mensagens mais antigas
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasOlderMessages && !loadingMore) {
          // Captura a altura antes de prepender, para reancorar o scroll.
          const container = messagesContainerRef.current;
          if (container) prevScrollHeightRef.current = container.scrollHeight;
          loadOlderMessages();
        }
      },
      { threshold: 0.1 }
    );
    const target = topObserverTarget.current;
    if (target) observer.observe(target);
    return () => { if (target) observer.unobserve(target); };
  }, [hasOlderMessages, loadingMore, loadOlderMessages]);

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  const transformMessage = (msg) => {
    let replyToData = null;
    if (msg.reply_to) {
      replyToData = {
        id: msg.reply_to.message_uuid || msg.reply_to.uuid,
        message_uuid: msg.reply_to.message_uuid || msg.reply_to.uuid,
        wa_message_id: msg.reply_to.wa_message_id,
        text: msg.reply_to.text,
        name: msg.reply_to.direction === 'OUT' ? 'Você' : conversation?.contact?.display_name,
      };
    }

    return {
      id: msg.uuid,
      uuid: msg.uuid,
      text: msg.text,
      timestamp: formatTime(msg.event_at),
      fullTimestamp: msg.event_at,
      event_at: msg.event_at,
      isSent: msg.direction === 'OUT',
      direction: msg.direction,
      message_type: msg.message_type,
      has_media: msg.has_media,
      sender: msg.sender,
      media: msg.media,
      reply_to: msg.reply_to,
      replyTo: replyToData,
    };
  };

  const olderMessages = olderPagesRaw.flat().map(transformMessage);
  const latestMessages = latestMessagesRaw.map(transformMessage);
  const displayedMessages = [...olderMessages, ...latestMessages];

  const scrollToMessage = (messageId) => {
    const el = messageRefs.current[messageId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.style.transition = 'background-color 0.3s';
      const originalBg = el.style.backgroundColor;
      el.style.backgroundColor = theme.alpha((theme.vars || theme).palette.primary.main, 0.15);
      setTimeout(() => { el.style.backgroundColor = originalBg; }, 1000);
    }
  };

  const handleBack = () => navigate(location.state?.from || '/leads');

  const handleAcceptLead = () => {
    setPendingAction('accept');
    acceptMutation.mutate(id, {
      onSuccess: () => navigate(`/leads/${id}`, { replace: true }),
      onError: () => setPendingAction(null),
    });
  };

  // Release/close/reopen tiram o colaborador da conversa ou mudam seu estado:
  // em todos os casos a prévia deixa de refletir a realidade, então volta à lista.
  const runAndLeave = (action, mutation) => () => {
    setPendingAction(action);
    mutation.mutate(id, { onSettled: () => navigate('/leads', { replace: true }) });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const customer = conversation?.contact || {};
  const lead = conversation?.contact ? transformConversation(conversation) : null;

  // Desktop: cápsula de ícones no cabeçalho. Mobile: quando o colaborador não pode
  // responder, o rodapé está livre (não há campo de mensagem) e recebe os botões
  // completos; podendo responder, as ações voltam para o menu compacto no topo.
  const atBottom = !isDesktop && Boolean(lead) && !lead.canSendMessages;
  const actions = lead && (
    <LeadActionsMenu
      lead={lead}
      variant={atBottom ? 'buttons' : (isDesktop ? 'inline' : 'menu')}
      pendingAction={pendingAction}
      disabled={Boolean(pendingAction)}
      onAccept={handleAcceptLead}
      onRelease={runAndLeave('release', releaseMutation)}
      onClose={runAndLeave('close', closeMutation)}
      onReopen={runAndLeave('reopen', reopenMutation)}
      onTransfer={() => setTransferDialogOpen(true)}
    />
  );

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <ChatHeader
        customer={customer}
        onBack={handleBack}
        isPreview={true}
        actions={atBottom ? null : actions}
      />

      {/* Preview Badge */}
      <Box
        sx={{
          bgcolor: 'primary.dark',
          color: 'primary.contrastText',
          py: 1,
          px: 2,
          textAlign: 'center',
          typography: 'subtitle2',
        }}
      >
        {t('chat.readOnly')}
      </Box>

      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          // Fundo neutro da conversa (o da página é azulado): o papel com 4% de
          // preto, que escurece nos dois esquemas (#F5F5F5 no light, #111 no
          // dark). O `action.hover` é branco no dark e deixava a área mais clara
          // que o balão recebido. `color-mix` porque o `theme.alpha` com CSS vars
          // precisa de um canal, e o MUI não gera um para o `common.black`.
          bgcolor: (theme) => {
            const { background, common } = (theme.vars || theme).palette;
            return `color-mix(in srgb, ${background.paper} 96%, ${common.black})`;
          },
        }}
      >
        <div ref={topObserverTarget} style={{ height: '1px' }} />

        {loadingMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {displayedMessages.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Typography color="text.secondary">{t('chat.noMessages')}</Typography>
          </Box>
        ) : (
          <>
            {displayedMessages.map((msg, index) => {
              const prevMsg = index > 0 ? displayedMessages[index - 1] : null;
              const showDateSeparator = !prevMsg ||
                new Date(msg.fullTimestamp).toDateString() !== new Date(prevMsg.fullTimestamp).toDateString();

              return (
                <React.Fragment key={msg.id}>
                  {showDateSeparator && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          bgcolor: 'action.selected',
                          px: 2,
                          py: 0.5,
                          borderRadius: 2,
                        }}
                      >
                        {formatDate(msg.fullTimestamp, i18n.language)}
                      </Typography>
                    </Box>
                  )}
                  <ChatMessage
                    msg={msg}
                    messageRef={(el) => (messageRefs.current[msg.id] = el)}
                    scrollToMessage={scrollToMessage}
                    onCopied={() => setCopied(true)}
                  />
                </React.Fragment>
              );
            })}
          </>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {atBottom && (
        <Box px={2} py={1.5} bgcolor="background.paper" borderTop={1} borderColor="divider">
          {actions}
        </Box>
      )}

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message={t('chat.messageCopied')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <TransferLeadDialog
        open={transferDialogOpen}
        onClose={() => setTransferDialogOpen(false)}
        lead={lead}
        onTransferSuccess={() => navigate('/leads', { replace: true })}
      />
    </Box>
  );
};

export default ChatPreview;
